import { useTranslation } from "@sagot/i18n";
import { Button, Field, Input, Select, Skeleton } from "@sagot/ui";
import { useState } from "react";
import { useDebounced } from "../../../../lib/slug";
import { useCrawlPages } from "../use-article-sources";

const A = "engage.workflows.article.page";

interface PageFieldProps {
  sourceId: string;
  value: string | undefined;
  disabled: boolean;
  onChange: (pageId: string | undefined) => void;
}

/** An optional page of the chosen website: the card then links that page (its title and address) instead of the site's main link. */
export function ArticlePageField({ sourceId, value, disabled, onChange }: PageFieldProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const query = useDebounced(search.trim());
  const pages = useCrawlPages(sourceId, query, true);
  if (pages.isPending) return <Skeleton role="status" aria-label={t(`${A}.loading`)} className="h-10 w-full" />;
  if (pages.isError) {
    return (
      <p className="flex flex-wrap items-center gap-2 rounded-md bg-surface-2 p-3 text-xs text-fg-muted" role="note">
        {t(`${A}.loadFailed`)}
        <Button type="button" variant="secondary" size="sm" onClick={() => void pages.refetch()}>
          {t(`${A}.retry`)}
        </Button>
      </p>
    );
  }
  const found = pages.data;
  const listed = found.some((page) => page.id === value);
  if (found.length === 0 && query === "" && value === undefined) return <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted" role="note">{t(`${A}.none`)}</p>;
  return (
    <div className="space-y-2">
      <Field label={t(`${A}.search`)}>
        <Input type="search" value={search} disabled={disabled} placeholder={t(`${A}.searchPlaceholder`)} onChange={(event) => { setSearch(event.target.value); }} />
      </Field>
      <Field label={t(`${A}.label`)} hint={t(`${A}.hint`)}>
        <Select value={value ?? ""} disabled={disabled} onChange={(event) => { onChange(event.target.value === "" ? undefined : event.target.value); }}>
          <option value="">{t(`${A}.wholeSite`)}</option>
          {value !== undefined && !listed ? <option value={value}>{t(`${A}.unavailableOption`)}</option> : null}
          {found.map((page) => <option key={page.id} value={page.id}>{page.title ?? page.url}</option>)}
        </Select>
      </Field>
      {found.length === 0 && query !== "" ? <p className="text-xs text-fg-muted" role="status">{t(`${A}.noMatches`, { query })}</p> : null}
    </div>
  );
}
